import { Fragment, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import type {
  ProductionVendorBank,
  ProductionVendorBasic,
  ProductionVendorContent,
  ProductionVendorFactory,
  ProductionVendorVtpm,
} from '@/utils/approval/productionVendor';
import { ProductionVendorRoot } from './styles';

const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

/** 레거시 number_format — 숫자가 아니면 원문 */
const formatNumber = (v: unknown) => {
  const raw = text(v);
  const n = Number(raw.replace(/,/g, ''));
  return raw && Number.isFinite(n) ? n.toLocaleString('en-US') : raw;
};

const isEmptyPayDate = (v: unknown) => ['', '-'].includes(text(v));

const CANCEL = 'cancel';
const CHANGED = 'changed';

type BasicRow = {
  label: string;
  field: keyof ProductionVendorBasic;
  diff: string;
};

const BASIC_ROWS: BasicRow[] = [
  { label: '거래처코드', field: 'custCode', diff: 'code' },
  { label: '거래처명', field: 'custName', diff: 'name' },
  { label: '대표자', field: 'repName', diff: 'rep' },
  { label: '사업자등록번호', field: 'busoNo', diff: 'buso' },
  { label: '법인번호', field: 'corpNo', diff: 'corp' },
  { label: '종목', field: 'busiType', diff: 'type' },
  { label: '업태', field: 'busiCond', diff: 'cond' },
  { label: '우편번호', field: 'post', diff: 'post' },
  { label: '주소', field: 'addr', diff: 'addr' },
  { label: '전화번호', field: 'tel', diff: 'tel' },
  { label: '이메일', field: 'email', diff: 'email' },
  { label: '블랙리스트 여부', field: 'black', diff: 'black' },
  { label: '블랙리스트 사유', field: 'blackReason', diff: 'reason' },
];

/** 레거시: 한 줄에 3개씩, 읽기 전용 체크 */
const VtpmCell = ({ items }: { items: ProductionVendorVtpm[] }) => (
  <td>
    {items.map((item, i) => (
      <Fragment key={`${item.dtlCd}-${i}`}>
        <label className="vtpm-item">
          <input
            type="checkbox"
            disabled
            readOnly
            checked={text(item.checked) === 'Y'}
          />{' '}
          {text(item.dtlCdNm)}
        </label>
        {i % 3 === 2 && <br />}
      </Fragment>
    ))}
  </td>
);

/** 레거시: 변경 전 결재일은 '일' 없이 항상, 그 외는 '일'을 붙이고 비어 있으면 줄 생략 */
const BankLines = ({
  bank,
  isBefore,
  t,
}: {
  bank: ProductionVendorBank;
  isBefore: boolean;
  t: TFunction;
}) => {
  const payDate = text(bank.payDate);
  const lines = [
    [t('은행'), text(bank.bankCode)],
    [t('계좌번호'), text(bank.bankAccount)],
    [t('수금유형'), text(bank.payMethod)],
    [t('지급기간'), text(bank.payWhen)],
    [t('국가'), text(bank.bankCountry)],
    ...(isBefore
      ? [[t('결재일'), payDate]]
      : isEmptyPayDate(payDate)
      ? []
      : [[t('결재일'), payDate.endsWith('일') ? payDate : `${payDate}일`]]),
    [t('사용유무'), text(bank.useYn)],
  ];
  return (
    <>
      {lines.map(([label, value], i) => (
        <Fragment key={label}>
          {i > 0 && <br />}
          {label} : {value}
        </Fragment>
      ))}
    </>
  );
};

const BasicTable = ({
  content,
  isChange,
  t,
}: {
  content: ProductionVendorContent;
  isChange: boolean;
  t: TFunction;
}) => {
  const before = isChange ? content.basicBefore : null;
  const after = content.basicAfter;
  const diff = isChange ? content.basicDiff ?? {} : {};
  const bankBefore = isChange ? content.bankBefore ?? [] : [];
  const bankAfter = content.bankAfter ?? [];
  const bankRows = Math.max(bankBefore.length, bankAfter.length);
  const vtpmRight = content.vtpmRight ?? [];
  const vtpmLeft = isChange ? content.vtpmLeft ?? [] : [];
  return (
    <section>
      <table className="text-center">
        <colgroup>
          <col width="20%" />
          <col width="40%" />
          <col width="40%" />
        </colgroup>
        <tbody>
          <tr>
            <th>{t('항목')}</th>
            <th>{t('변경 전')}</th>
            <th>{t('변경 후')}</th>
          </tr>
          {BASIC_ROWS.map(({ label, field, diff: key }) => {
            const changed = diff[key] === true;
            return (
              <tr key={field}>
                <td>{t(label)}</td>
                <td className={changed ? CANCEL : undefined}>
                  {text(before?.[field])}
                </td>
                <td className={changed ? CHANGED : undefined}>
                  {text(after?.[field])}
                </td>
              </tr>
            );
          })}
          <tr>
            <td>{t('거래유형')}</td>
            <VtpmCell items={vtpmLeft} />
            <VtpmCell items={vtpmRight} />
          </tr>
          {Array.from({ length: bankRows }, (_, i) => (
            <tr key={i}>
              <td>
                {t('계좌')}({i + 1})
              </td>
              <td className="text-left">
                {bankBefore[i] && (
                  <span className={CANCEL}>
                    <BankLines bank={bankBefore[i]} isBefore t={t} />
                  </span>
                )}
              </td>
              <td className="text-left">
                {bankAfter[i] && (
                  <span className={isChange ? CHANGED : undefined}>
                    <BankLines bank={bankAfter[i]} isBefore={false} t={t} />
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

const BrandTable = ({
  content,
  t,
}: {
  content: ProductionVendorContent;
  t: TFunction;
}) => (
  <section>
    <table className="text-center">
      <colgroup>
        <col width="50%" />
        <col width="50%" />
      </colgroup>
      <tbody>
        <tr>
          <th>{t('브랜드')}</th>
          <th>{t('거래 상태')}</th>
        </tr>
        {(content.brands ?? []).map((brand, i) => (
          <tr key={`${brand.brand}-${i}`}>
            <td>{text(brand.nameKor)}</td>
            <td className={brand.stopped ? CHANGED : undefined}>
              {brand.stopped ? t('거래중지') : t('거래중')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);

const TradeStartReasonTable = ({
  reason,
  t,
}: {
  reason: string;
  t: TFunction;
}) => (
  <section>
    <table className="text-center">
      <colgroup>
        <col width="50%" />
        <col width="50%" />
      </colgroup>
      <tbody>
        <tr>
          <th>{t('거래개시 사유')}</th>
          <th>{reason}</th>
        </tr>
      </tbody>
    </table>
  </section>
);

const HistoryCardTable = ({
  content,
  t,
}: {
  content: ProductionVendorContent;
  t: TFunction;
}) => {
  const card = content.card01 ?? {};
  return (
    <section>
      <table>
        <colgroup>
          <col width="12%" />
          <col width="13%" />
          <col width="12%" />
          <col width="13%" />
          <col width="10%" />
          <col width="15%" />
          <col width="12%" />
          <col width="13%" />
        </colgroup>
        <tbody>
          <tr>
            <th colSpan={8}>{t('국내 협력회사 이력카드 (신규)')}</th>
          </tr>
          <tr>
            <td className="label">{t('브랜드')}</td>
            <td colSpan={2}>{text(content.accountBrand?.codevalue)}</td>
            <td className="label">{t('작성일')}</td>
            <td colSpan={4}>{text(content.today)}</td>
          </tr>
          <tr>
            <td className="label">{t('회사명')}</td>
            <td colSpan={2}>{text(card.custName)}</td>
            <td className="label">{t('사업자번호')}</td>
            <td colSpan={2}>{text(card.busiNo)}</td>
            <td className="label">{t('코드')}</td>
            <td>{text(card.custCode)}</td>
          </tr>
          <tr>
            <td className="label">{t('주소')}</td>
            <td colSpan={7}>{text(card.addr)}</td>
          </tr>
          <tr>
            <td className="label">{t('업태')}</td>
            <td colSpan={2}>{text(card.busiCond)}</td>
            <td className="label">{t('종목')}</td>
            <td colSpan={2}>{text(card.busiType)}</td>
            <td />
            <td />
          </tr>
          <tr>
            <td className="label">{t('대표자명')}</td>
            <td colSpan={2}>{text(card.repName)}</td>
            <td className="label">{t('주민번호')}</td>
            <td colSpan={2} />
            <td className="label">E-MAIL</td>
            <td>{text(card.repEmail)}</td>
          </tr>
          <tr>
            <td className="label">TEL</td>
            <td>{text(card.tel)}</td>
            <td className="label">FAX</td>
            <td>{text(card.fax)}</td>
            <td className="label">{t('자택')}</td>
            <td>{text(card.repTel)}</td>
            <td className="label">{t('휴대폰')}</td>
            <td />
          </tr>
          <tr>
            <td className="label">{t('사업형태')}</td>
            <td colSpan={7}>{text(card.busitypeText)}</td>
          </tr>
          <tr>
            <td className="label">{t('전문분야')}</td>
            <td colSpan={5}>
              {t('영역')}: {text(card.specField)}&nbsp;&nbsp;&nbsp;{t('복종')}:{' '}
              {text(card.specItem)}
            </td>
            <td className="label">{t('거래형태')}</td>
            <td>{text(card.tradeType)}</td>
          </tr>
          <tr>
            <td className="label">{t('회사규모')}</td>
            <td colSpan={7}>
              {t('회사규모입력')} ({t('연간거래외형')} -{' '}
              {formatNumber(card.busiTurnover)}) &nbsp;&nbsp;&nbsp; ({t('인원')}{' '}
              - {text(card.busiPerson)}) &nbsp;&nbsp;&nbsp; ({t('월 생산 CAPA')}{' '}
              - {formatNumber(card.busiCapa)})
            </td>
          </tr>
          {(
            [
              ['기획(디자인 부문)', card.designNum],
              ['영업부문', card.salesNum],
              ['지원부문', card.suppNum],
            ] as const
          ).map(([label, num], i) => (
            <tr key={label}>
              {i === 0 && (
                <td className="label" rowSpan={3}>
                  {t('사무실 구성')}
                </td>
              )}
              <td>{t(label)}</td>
              <td colSpan={5} />
              <td>{t('계 {{num}} 명', { num: text(num) })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

const FactoryStatusTable = ({
  content,
  t,
}: {
  content: ProductionVendorContent;
  t: TFunction;
}) => {
  const factories = content.card02 ?? [];
  return (
    <section>
      <table>
        <colgroup>
          <col width="10%" />
          <col width="10%" />
          <col width="10%" />
          <col width="15%" />
          <col width="15%" />
          <col width="20%" />
          <col width="10%" />
          <col width="10%" />
        </colgroup>
        <tbody>
          {factories.map((f, i) => (
            <Fragment key={`${f.seq}-${i}`}>
              <tr>
                {i === 0 && (
                  <td className="label" rowSpan={factories.length * 4}>
                    {t('공장현황')}
                  </td>
                )}
                <td className="label" rowSpan={4}>
                  {text(f.gubunTypeDesc)}
                </td>
                <td className="label">{t('공장명')}</td>
                <td>{text(f.manuName)}</td>
                <td className="label">{t('주소')}</td>
                <td>{text(f.manuAddr)}</td>
                <td className="label">{t('대표자')}</td>
                <td>{text(f.repName)}</td>
              </tr>
              <tr>
                <td className="label">{t('직영여부')}</td>
                <td colSpan={3}>{text(f.manageTypeDesc)}</td>
                <td />
                <td />
              </tr>
              <tr>
                <td className="label">{t('활용정도')}</td>
                <td colSpan={3}>
                  {t('공장 총 {{total}} 라인중 {{used}} 라인 활용', {
                    total: text(f.tLine),
                    used: text(f.pLine),
                  })}
                </td>
                <td className="label">{t('활용율')}</td>
                <td>{text(f.rate)}%</td>
              </tr>
              <tr>
                <td className="label">{t('공장설비요약')}</td>
                <td colSpan={5}>{text(f.remark)}</td>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </section>
  );
};

/** 레거시: 당사(A)·타사(B) 각 3줄 고정 */
const SalesStatusTable = ({
  content,
  t,
}: {
  content: ProductionVendorContent;
  t: TFunction;
}) => {
  const rows = content.card03 ?? [];
  const groups = [
    { label: '당사', items: rows.filter((r) => text(r.gubun) === 'A') },
    { label: '타사', items: rows.filter((r) => text(r.gubun) === 'B') },
  ];
  return (
    <section>
      <table>
        <colgroup>
          <col width="10%" />
          <col width="10%" />
          <col width="20%" />
          <col width="20%" />
          <col width="20%" />
          <col width="20%" />
        </colgroup>
        <tbody>
          {groups.map((group, g) => (
            <Fragment key={group.label}>
              <tr>
                {g === 0 && (
                  <td className="label" rowSpan={8}>
                    {t('영업현황')}
                  </td>
                )}
                <td className="label" rowSpan={4}>
                  {t(group.label)}
                </td>
                <td className="label">{t('개시 년월')}</td>
                <td className="label">{t('브랜드')}</td>
                <td className="label">{t('거래실적')}</td>
                <td className="label">{t('비고')}</td>
              </tr>
              {[0, 1, 2].map((i) => (
                <tr key={i}>
                  <td>{text(group.items[i]?.ym)}</td>
                  <td>{text(group.items[i]?.brand)}</td>
                  <td className="text-right">
                    {group.items[i]?.tradeAmt
                      ? formatNumber(group.items[i].tradeAmt)
                      : ''}
                  </td>
                  <td>{text(group.items[i]?.remark)}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </section>
  );
};

type AuditItem = {
  category?: { label: string; rowSpan: number };
  item?: { label: string; rowSpan?: number };
  criteria: string;
  field: keyof ProductionVendorFactory;
  /** PL 보험은 등급이 아니라 가입 여부 원문 */
  raw?: boolean;
};

const AUDIT_ITEMS: AuditItem[] = [
  {
    category: { label: '의식', rowSpan: 3 },
    item: { label: '대표자 마인드' },
    criteria: '의욕 및 품질 마인드',
    field: 'aMind',
  },
  {
    item: { label: '청결, 안전' },
    criteria: '청결상태 및 전기배선 등 안전여부',
    field: 'aSafety',
  },
  {
    item: { label: '품질의식' },
    criteria: '품질위주 공장 또는 양 위주 공장',
    field: 'aQuality',
  },
  {
    category: { label: '생산품질', rowSpan: 6 },
    item: { label: '자재관리상태', rowSpan: 2 },
    criteria: '연단/검단 실시유무',
    field: 'bJaCheck',
  },
  { criteria: '원사관리 상태(습기, 보관위치)', field: 'bJaManage' },
  {
    item: { label: '설비수준' },
    criteria: '최신설비 및 특종기계 유무',
    field: 'bEquipment',
  },
  {
    item: { label: '작업자 숙련도' },
    criteria: '작업자 경력 및 숙련정도',
    field: 'bSkill',
  },
  {
    item: { label: '작업 process' },
    criteria: '일의 순서, 절차와 흐름의 경쟁력',
    field: 'bProcess',
  },
  {
    item: { label: '작업감독, 지도' },
    criteria: '감독자 유무, 기술지도 유무',
    field: 'bSupervisor',
  },
  {
    category: { label: '관리능력', rowSpan: 6 },
    item: { label: '검사체계' },
    criteria: '전담검사원 확보/검사실시상태/검사기록유지',
    field: 'cInspection',
  },
  {
    item: { label: '외주관리' },
    criteria: '부속 등 외주공장 관리능력(자수처, washing처)',
    field: 'cOutsourcing',
  },
  {
    item: { label: '불량관리' },
    criteria: '불량품 식별관리능력',
    field: 'cBad',
  },
  {
    item: { label: '설비점검체계' },
    criteria: '작업전 설비를 정기점검 하는가?',
    field: 'cEqCheck',
  },
  {
    item: { label: '자재 defect관리' },
    criteria: 'defect 발견시 처리절차',
    field: 'cDefect',
  },
  {
    item: { label: '부자재 관리수준' },
    criteria: '부자재 전담원 유무, 기록상태, 보관환경',
    field: 'cSubjaCheck',
  },
  {
    category: { label: '정리정돈', rowSpan: 3 },
    item: { label: '작업물 관리' },
    criteria: '보관위치 지정여부, 넘버링 여부',
    field: 'dKeepManage',
  },
  {
    item: { label: '작업동선' },
    criteria: '작업동선은 효율적인가',
    field: 'dJobMoving',
  },
  {
    item: { label: '작업장조명' },
    criteria: '현장조명은 충분한가',
    field: 'dJobLight',
  },
  {
    category: { label: 'PL법 대응', rowSpan: 2 },
    item: { label: '검침기' },
    criteria: '보유여부, 적용여부',
    field: 'eInspect',
  },
  {
    item: { label: 'pl보험' },
    criteria: '가입유무',
    field: 'ePl',
    raw: true,
  },
];

const GRADES = ['A', 'B', 'C'] as const;

const FactoryAuditTable = ({
  factory,
  t,
}: {
  factory: ProductionVendorFactory;
  t: TFunction;
}) => (
  <section>
    <table>
      <colgroup>
        <col width="14%" />
        <col width="14%" />
        <col width="18%" />
        <col width="30%" />
        <col width="8%" />
        <col width="8%" />
        <col width="8%" />
      </colgroup>
      <tbody>
        <tr>
          <td colSpan={2}>{t('1. 객관적 평가항목')}</td>
          <td className="label">{t('공장명')}</td>
          <td>{text(factory.manuName)}</td>
          <td className="label">{t('심사일')}</td>
          <td colSpan={2}>{text(factory.judgedate)}</td>
        </tr>
        <tr>
          <td className="label text-center" rowSpan={2} colSpan={2}>
            {t('구분')}
          </td>
          <td className="label text-center" rowSpan={2} colSpan={2}>
            {t('심사항목')}
          </td>
          <td className="label text-center" colSpan={3}>
            {t('평가')}
          </td>
        </tr>
        <tr>
          {GRADES.map((g) => (
            <td key={g} className="label text-center">
              {g}
            </td>
          ))}
        </tr>
        {AUDIT_ITEMS.map(({ category, item, criteria, field, raw }) => {
          const value = text(factory[field]);
          return (
            <tr key={field}>
              {category && (
                <td className="label text-center" rowSpan={category.rowSpan}>
                  {t(category.label)}
                </td>
              )}
              {item && <td rowSpan={item.rowSpan}>{t(item.label)}</td>}
              <td colSpan={2}>{t(criteria)}</td>
              {raw ? (
                <>
                  <td className="text-center">{value}</td>
                  <td />
                  <td />
                </>
              ) : (
                GRADES.map((g) => (
                  <td key={g} className="text-center">
                    {value === g ? g : ''}
                  </td>
                ))
              )}
            </tr>
          );
        })}
        <tr>
          <td colSpan={7}>{t('2. 서술적 평가항목')}</td>
        </tr>
        {(
          [
            ['설비수준', factory.eqLevel],
            ['작업수준', factory.jobLevel],
            ['품질수준', factory.quLevel],
            ['향후 활용계획', factory.planRemark],
          ] as const
        ).map(([label, value]) => (
          <tr key={label}>
            <td className="label text-center">{t(label)}</td>
            <td colSpan={6}>{text(value)}</td>
          </tr>
        ))}
        <tr>
          <td colSpan={5} />
          <td className="label text-center">{t('평가자')}</td>
          <td>{text(factory.valuer)}</td>
        </tr>
      </tbody>
    </table>
  </section>
);

const SectionTitleTable = ({ title }: { title: string }) => (
  <section>
    <table>
      <tbody>
        <tr>
          <th>{title}</th>
        </tr>
      </tbody>
    </table>
  </section>
);

const CreditTable = ({
  content,
  t,
}: {
  content: ProductionVendorContent;
  t: TFunction;
}) => (
  <section>
    <table>
      <colgroup>
        <col width="20%" />
        <col width="20%" />
        <col width="20%" />
        <col width="40%" />
      </colgroup>
      <tbody>
        {(content.pay ?? []).map((p, i) => (
          <Fragment key={i}>
            <tr>
              <td className="label text-center">{t('신용코드')}</td>
              <td>{text(p.creditCode)}</td>
              <td>{text(p.creditNm01)}</td>
              <td>{text(p.creditNm02)}</td>
            </tr>
            <tr>
              <td className="label text-center">{t('현금코드')}</td>
              <td>{text(p.cashCode)}</td>
              <td>{text(p.cashNm01)}</td>
              <td>{text(p.cashNm02)}</td>
            </tr>
          </Fragment>
        ))}
      </tbody>
    </table>
  </section>
);

const RELATION_HEADERS = [
  '거래처 담당자',
  '임직원',
  '혈연',
  '학연',
  '지연',
  '전 직장동료',
  '전 직장 거래처',
  '자사(전 현직 임직원)',
  '기타',
  '비고',
];

const RelationTable = ({
  content,
  t,
}: {
  content: ProductionVendorContent;
  t: TFunction;
}) => (
  <section>
    <table className="text-center">
      <tbody>
        <tr>
          {RELATION_HEADERS.map((h) => (
            <th key={h}>{t(h)}</th>
          ))}
        </tr>
        {(content.rel ?? []).map((r, i) => (
          <tr key={i}>
            <td>
              {text(r.custCode)}
              <br />
              {text(r.custName)}
            </td>
            <td>
              {text(r.empNo)}
              <br />
              {text(r.empNoName)}
            </td>
            <td>{text(r.typeBlood)}</td>
            <td>{text(r.typeSchool)}</td>
            <td>{text(r.typeRegion)}</td>
            <td />
            <td />
            <td />
            <td>{text(r.typeEtc)}</td>
            <td>{text(r.remarks)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);

/** 레거시 appr_sub_view_forms_account_esg_view 와 같은 표 순서·노출 조건 */
const ProductionVendorContentView = ({
  content,
  t,
}: {
  content: ProductionVendorContent;
  t: TFunction;
}) => {
  const isChange = content.aprvTypcd === 'EMOD';
  const isNew = content.aprvTypcd === 'EEVE';
  const reason = text(content.tradeStartReason);
  return (
    <>
      {(content.basicBefore || content.basicAfter) && (
        <BasicTable content={content} isChange={isChange} t={t} />
      )}
      {!!content.brands?.length && <BrandTable content={content} t={t} />}
      {!isChange && reason && <TradeStartReasonTable reason={reason} t={t} />}
      {isNew && (
        <>
          {content.card01 && <HistoryCardTable content={content} t={t} />}
          {!!content.card02?.length && (
            <FactoryStatusTable content={content} t={t} />
          )}
          {!!content.card03?.length && (
            <SalesStatusTable content={content} t={t} />
          )}
          {!!content.factory?.length && (
            <>
              <SectionTitleTable title={t('공장 심사기준(봉제,편물)')} />
              {content.factory.map((f, i) => (
                <FactoryAuditTable key={`${f.seq}-${i}`} factory={f} t={t} />
              ))}
            </>
          )}
          {!!content.pay?.length && (
            <>
              <SectionTitleTable title={t('업체신용관리')} />
              <CreditTable content={content} t={t} />
            </>
          )}
        </>
      )}
      {!!content.rel?.length && (
        <>
          <SectionTitleTable title={t('업체 이해관계')} />
          <RelationTable content={content} t={t} />
        </>
      )}
    </>
  );
};

/** attributes.content — 조회 화면은 이 HTML 을 그대로 보여 줌 */
export const buildProductionVendorContentHtml = (
  content: ProductionVendorContent,
  t: TFunction,
): string =>
  renderToStaticMarkup(<ProductionVendorContentView content={content} t={t} />);

/** 저장 본문 그대로(레거시 문서 포함). 레거시 본문에 같이 저장된 스크립트는 버림 */
export const ProductionVendorSnapshot = ({ content }: { content: string }) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      if (tagName !== 'input' || element.getAttribute('type') !== 'checkbox')
        return undefined;
      return (
        <Checkbox
          key={props.key as string}
          size="small"
          disabled
          checked={element.hasAttribute('checked')}
        />
      );
    },
    [],
  );
  return (
    <ProductionVendorRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </ProductionVendorRoot>
  );
};
